import { Button, Group, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useEffect, useRef, useState } from 'react';
import { FirebaseRefs, useFirebaseRefs } from '../../lib/firebase';
import { updateUserClass } from '../../lib/userdata';
import { mergePrivateUserDataByUid } from '../../lib/hooks';
import { SheetsData } from '../Sheets';
import { getLogger } from '../../lib/logger';
import { useKeyboardScrollEffect } from '../../lib/keyboard';
import { blurOnEnterKey } from '../../lib/eventHandlers';

const logger = getLogger('displayName');

const MAX_DISPLAY_NAME_LENGTH = 60;

// The onboarding prompt is suppressed once the user has dismissed it in some deliberate way —
// saving a name, "Maybe later", the header X, or a backdrop tap (see the unmount effect below). It
// is deliberately not marked when the sheet is merely displayed, so a prompt the user never got to
// at all (app backgrounded, sheet preempted before it rendered) still comes back.
//
// The authoritative record is `displayNamePromptDismissed` on the user's private-user-data doc, so
// the decision follows the account to other devices and survives a reinstall. This localStorage
// mirror is a fast local check that avoids showing the sheet during the window before the private
// doc resolves, and a fallback if the server write fails.
const PROMPT_SEEN_KEY_PREFIX = 'displayNamePromptSeen:';

export function hasSeenDisplayNamePrompt(uid: string): boolean {
    if (typeof window === 'undefined') {
        return true; // never prompt outside the browser
    }
    try {
        return window.localStorage.getItem(`${PROMPT_SEEN_KEY_PREFIX}${uid}`) === '1';
    } catch {
        return false;
    }
}

function rememberPromptSeenLocally(uid: string): void {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        window.localStorage.setItem(`${PROMPT_SEEN_KEY_PREFIX}${uid}`, '1');
    } catch {
        /* ignore quota / serialization errors */
    }
}

// Records that the user has answered the prompt, locally and server-side. The local write happens
// first and unconditionally so the prompt stays suppressed on this device even if the network call
// fails; a failed server write only costs us a re-prompt on another device.
async function markDisplayNamePromptAnswered(firebaseRefs: FirebaseRefs, uid: string | undefined) {
    if (!uid) {
        return;
    }
    rememberPromptSeenLocally(uid);
    try {
        await mergePrivateUserDataByUid(firebaseRefs, uid, { displayNamePromptDismissed: true });
    } catch (err) {
        logger.warn(`Failed to persist display-name prompt dismissal for uid=${uid}: ${err}`);
    }
}

function DisplayNameForm({ uid, initialValue, isOnboarding, onClose }: {
    uid: string | undefined,
    initialValue: string,
    isOnboarding: boolean,
    onClose: () => void,
}) {
    const firebaseRefs = useFirebaseRefs();
    const [ saving, setSaving ] = useState(false);

    // On iOS the software keyboard overlays the sheet, which would cover this input. Use the same
    // mechanism as the app's other input sheets: the `keyboard-height-padded` class below pads the
    // content by the keyboard height (the --keyboard-height custom property maintained in _app),
    // and this hook scrolls the field back into view when the keyboard appears. The onFocus below
    // is a belt-and-braces scroll and must sit *after* the getInputProps spread, which supplies its
    // own onFocus and would otherwise override it.
    const inputTextRef = useRef<HTMLInputElement>(null);
    useKeyboardScrollEffect(inputTextRef);

    // The sheet can also be closed with its header X or by tapping the backdrop, which bypass both
    // buttons below. Those are still deliberate dismissals, so record the answer on unmount for
    // whichever path got us here — otherwise the onboarding prompt reappeared on every subsequent
    // Home mount. Save and "Maybe later" set this first so they don't record twice.
    const answeredRef = useRef(false);
    useEffect(() => () => {
        if (isOnboarding && !answeredRef.current) {
            answeredRef.current = true;
            void markDisplayNamePromptAnswered(firebaseRefs, uid);
        }
    }, [ isOnboarding, firebaseRefs, uid ]);

    const form = useForm({
        mode: 'uncontrolled',
        initialValues: { displayName: initialValue },
        validate: {
            displayName: (value: string) => {
                const trimmed = value.trim();
                if (!trimmed) {
                    return 'Please enter a Name';
                }
                if (trimmed.length > MAX_DISPLAY_NAME_LENGTH) {
                    return `Please keep it under ${MAX_DISPLAY_NAME_LENGTH} characters`;
                }
                return null;
            },
        },
    });

    const save = async (values: { displayName: string }) => {
        const displayName = values.displayName.trim();
        setSaving(true);
        try {
            await updateUserClass(firebaseRefs, uid, { displayName });
            // Answering by saving also settles the prompt. Having a display name suppresses it on
            // its own, so this is belt-and-braces and needn't be awaited — it records locally
            // straight away and Firestore queues the server write.
            if (isOnboarding) {
                answeredRef.current = true;
                void markDisplayNamePromptAnswered(firebaseRefs, uid);
            }
            notifications.show({ message: 'Name saved.' });
            onClose();
        } catch (err) {
            logger.warn(`Failed to save display name: ${err}`);
            notifications.show({ color: 'red', message: 'Could not save your Name. Please try again.' });
            setSaving(false);
        }
    };

    // "Maybe later" — an explicit decline, so record it and don't ask again. Not awaited: the
    // local write inside is synchronous, so the sheet closes immediately rather than waiting on the
    // network, and Firestore queues the server write (including when offline).
    const skip = () => {
        answeredRef.current = true;
        void markDisplayNamePromptAnswered(firebaseRefs, uid);
        onClose();
    };

    return (
        <form onSubmit={form.onSubmit(save)}>
            <Stack gap="md" p="xs" className="keyboard-height-padded">
                {isOnboarding && <Text size="sm">
                    You can change it anytime in Settings.
                </Text>}
                <TextInput
                    withAsterisk
                    ref={inputTextRef}
                    aria-label="Name"
                    label="Name"
                    placeholder="e.g. Karen Lucas"
                    maxLength={MAX_DISPLAY_NAME_LENGTH}
                    data-autofocus
                    enterKeyHint="done"
                    onKeyUp={blurOnEnterKey}
                    {...form.getInputProps('displayName')}
                    onFocus={() => inputTextRef.current?.scrollIntoView()}
                />
                <Group justify="flex-end" gap="sm">
                    {isOnboarding && <Button variant="subtle" disabled={saving} onClick={skip}>
                        Maybe later
                    </Button>}
                    {!isOnboarding && <Button variant="subtle" disabled={saving} onClick={onClose}>
                        Cancel
                    </Button>}
                    <Button type="submit" loading={saving}>
                        Save
                    </Button>
                </Group>
            </Stack>
        </form>
    );
}

/**
 * Opens a sheet that collects or edits the user's display name. In onboarding mode it includes a
 * skippable "Maybe later" and explanatory copy; otherwise it's a plain editor with a Cancel action.
 */
export function showDisplayNamePrompt(sheets: SheetsData, options: {
    uid: string | undefined,
    initialValue?: string | null,
    isOnboarding?: boolean,
}) {
    const isOnboarding = options.isOnboarding ?? false;
    sheets.showSheetWith(
        context => <DisplayNameForm
            uid={options.uid}
            initialValue={options.initialValue?.trim() ?? ''}
            isOnboarding={isOnboarding}
            onClose={() => context.removeSheet()}
        />,
        {
            title: isOnboarding ? 'What should we call you?' : 'Edit Name',
            // Full height (TSSheet's default — no detent) so the iOS keyboard can't overlay the
            // field, the same approach the delete-guide confirmation sheet takes for its text
            // input. A content-height sheet sits at the bottom of the screen, right where the
            // keyboard appears.
            disableDrag: true,
        }
    );
}
